import type Map from 'ol/Map';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import type Geometry from 'ol/geom/Geometry';
import type Draw from 'ol/interaction/Draw';
import { liveShape, type LiveEffective, type LiveShape } from '../domain/liveMeasure';
import { LiveMeasureCard } from '../ui/LiveMeasureCard';

/**
 * V1.9.3 H · live measure of a drawing in progress. It listens to the sketch geometry of an existing OpenLayers Draw
 * (no second interaction, no pixel maths) and feeds a small DOM card; both exist only while the sketch does: the card
 * is an HTML overlay of the map (never part of the canvas, so never in a PNG) and nothing is stored.
 *
 * Cost: the card repaints at most once per animation frame. The «effective» surface (the drawing cut by the active
 * geometry of its parent) is the same function the final preview uses; it is throttled by the time it actually takes,
 * so a heavy parent only makes the figure refresh a little later, never the cursor lag.
 */
export interface LiveMeasureOptions {
  /** Polygons only: cuts the sketch by its parent. Same engine as the final preview (createSubareaPreview). */
  readonly effective?: (polygon: Geometry) => LiveEffective | null;
  /** «Dividir estado de Suerte»: also shows what is left of the Suerte. */
  readonly split?: boolean;
  /** Names the thing measured in the card header (e.g. «Área parcial»). */
  readonly title?: string;
}

const MIN_INTERVAL_MS = 90;

export class LiveMeasure {
  private readonly card: LiveMeasureCard;
  private keys: EventsKey[] = [];
  private frame = 0;
  private geometry: Geometry | null = null;
  private options: LiveMeasureOptions = {};
  private lastEffective: { readonly at: number; readonly value: LiveEffective | null } | null = null;
  private interval = MIN_INTERVAL_MS;
  private trailing = 0;
  private pointerKeys: EventsKey[] = [];

  constructor(private readonly map: Map) {
    this.card = new LiveMeasureCard(map.getTargetElement());
    this.card.onTargetChange(() => this.refresh());
  }

  get visible(): boolean { return this.card.visible; }
  get targetHa(): number | null { return this.card.targetHa; }

  /** Follows one Draw interaction until its sketch ends or is aborted. Returns a disposer for tool changes. */
  attach(draw: Draw, options: LiveMeasureOptions = {}): () => void {
    const start = draw.on('drawstart', ({ feature }) => this.begin(feature.getGeometry() ?? null, options));
    const end = draw.on('drawend', () => this.end());
    const abort = draw.on('drawabort', () => this.end());
    const keys = [start, end, abort].flat() as EventsKey[];
    return () => { unByKey(keys); this.end(); };
  }

  private begin(geometry: Geometry | null, options: LiveMeasureOptions): void {
    this.end();
    if (!geometry) return;
    this.geometry = geometry; this.options = options; this.lastEffective = null; this.interval = MIN_INTERVAL_MS;
    this.keys = [geometry.on('change', () => this.schedule())];
    // The card steps aside when the pointer comes close, so it never hides the cursor, a vertex or the polygon.
    const dodge = (event: { originalEvent: Event }) => this.card.dodge(event.originalEvent as MouseEvent);
    this.pointerKeys = [this.map.on('pointermove', dodge), this.map.on('click', dodge)];
    this.card.open(options.title ?? '');
    this.refresh();
  }

  private end(): void {
    unByKey(this.keys); this.keys = [];
    unByKey(this.pointerKeys); this.pointerKeys = [];
    if (this.frame) { cancelAnimationFrame(this.frame); this.frame = 0; }
    if (this.trailing) { clearTimeout(this.trailing); this.trailing = 0; }
    this.geometry = null; this.lastEffective = null; this.options = {};
    this.card.close();
  }

  private schedule(): void {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.refresh(); });
  }

  private refresh(): void {
    if (!this.geometry) return;
    const shape: LiveShape | null = liveShape(this.geometry);
    if (!shape) { this.card.show(null, null, false); return; }
    this.card.show(shape, shape.kind === 'polygon' ? this.effectiveFor(shape) : null, Boolean(this.options.split));
  }

  /** Effective surface of the sketch; between refreshes the last value is kept (and shown) instead of recomputing. */
  private effectiveFor(shape: Extract<LiveShape, { kind: 'polygon' }>): LiveEffective | null {
    const resolver = this.options.effective; if (!resolver || !this.geometry) return null;
    const now = performance.now(); const last = this.lastEffective;
    if (last && now - last.at < this.interval) {
      if (!this.trailing) this.trailing = window.setTimeout(() => { this.trailing = 0; this.refresh(); }, this.interval);
      return last.value;
    }
    const begun = performance.now();
    let value: LiveEffective | null = null;
    try { value = shape.areaM2 > 0 ? resolver(this.geometry) : null; } catch { value = null; }
    const cost = performance.now() - begun;
    this.interval = Math.min(400, Math.max(MIN_INTERVAL_MS, cost * 3)); // adapts to the real cost of the parent geometry
    this.lastEffective = { at: performance.now(), value };
    return value;
  }
}
